// node_modules
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
// Enums
import { CaseActionTypeEnum, SideNoteTypeEnum } from "Enums";
// Interfaces
import { IUpdateTechSideNoteListAction } from "Interfaces";
// Types
import { TNewSideNoteDTO, TNotificationMessageDTO, TSideNoteDTO } from "Types";
// Contexts
import { CaseContext, AuthContext } from "Stores";
import { WebsocketContext } from "./WebsocketProvider";
import { HubConstants } from "Constants";
import { ToastHelperSingleton } from "Helpers";

type TSideNotesContext = {
    sideNotes: TSideNoteDTO[],
    setNewSideNotes: (newSideNotes: TSideNoteDTO[], newSideNotesType: SideNoteTypeEnum) => void
}

export const SideNotesContext = createContext<TSideNotesContext>({} as TSideNotesContext);

export const SideNotesProvider = ({ children }: any) => {
    // State
    const [sideNotes, setSideNotes] = useState<TSideNoteDTO[]>([]);
    const [sideNotesType, setSideNotesType] = useState<SideNoteTypeEnum>(SideNoteTypeEnum.None);

    // Contexts
    const { caseDispatch } = useContext(CaseContext);
    const { webSocketController } = useContext(WebsocketContext);
    const { authState } = useContext(AuthContext);

    // Logic
    const setNewSideNotes = useCallback((newSideNotes: TSideNoteDTO[], newSideNotesType: SideNoteTypeEnum): void => {
        setSideNotesType(newSideNotesType);
        setSideNotes(newSideNotes);
    }, []);

    const addReplyToSideNote = useCallback((sideNoteToUpdate: TSideNoteDTO, reply: TSideNoteDTO): TSideNoteDTO[] => {
        for (const sideNote of sideNotes) {
            if (sideNote.id === sideNoteToUpdate.id) {
                sideNote.replies.push(reply);
                break;
            }
        }

        setSideNotes([...sideNotes]);
        return [...sideNotes];
    }, [sideNotes]);

    const updateSideNote = useCallback((sideNoteToUpdate: TSideNoteDTO): TSideNoteDTO[] => {
        for (const sideNote of sideNotes) {
            if (sideNote.id === sideNoteToUpdate.id) {
                sideNote.text = sideNoteToUpdate.text;
                break;
            }
        }

        setSideNotes([...sideNotes]);
        return [...sideNotes];
    }, [sideNotes]);

    const deleteSideNote = useCallback((sideNoteToDelete: TSideNoteDTO): TSideNoteDTO[] => {
        let sideNoteToDeleteIndex = -1;
        let isReplyToDelete = false;

        for (let indexSideNote = 0; indexSideNote < sideNotes.length; indexSideNote++) {
            const sideNote = sideNotes[indexSideNote];

            if (sideNote && sideNote.id === sideNoteToDelete.id) {
                sideNoteToDeleteIndex = indexSideNote;
                break;
            }

            if (sideNote.replies) {
                let replyToDeleteIndex = -1;

                for (let indexReply = 0; indexReply < sideNote.replies.length; indexReply++) {
                    const reply = sideNote.replies[indexReply];

                    if (reply && reply.id === sideNoteToDelete.id) {
                        isReplyToDelete = true;
                        replyToDeleteIndex = indexReply;
                        break;
                    }
                }

                if (replyToDeleteIndex > -1 && isReplyToDelete) {
                    sideNote.replies.splice(replyToDeleteIndex, 1);
                }
            }
        }

        if (sideNoteToDeleteIndex > -1 && !isReplyToDelete) {
            sideNotes.splice(sideNoteToDeleteIndex, 1);
        }
        setSideNotes([...sideNotes]);
        return [...sideNotes];
    }, [sideNotes]);

    const unshiftSideNote = useCallback((sideNoteToAdd: TSideNoteDTO): TSideNoteDTO[] => {
        sideNotes.unshift(sideNoteToAdd);
        setSideNotes([...sideNotes]);
        return [...sideNotes];
    }, [sideNotes]);


    const updateCaseContext = useCallback((sideNoteType: SideNoteTypeEnum, technologyId: number | undefined, newSideNoteList: TSideNoteDTO[]): void => {
        // update side note list on the specific technology in the CaseContext 
        // (in order to re-render components which use the concept technology list/additional results)
        if (sideNoteType === SideNoteTypeEnum.TechnologyNote && technologyId) {
            caseDispatch({
                type: CaseActionTypeEnum.UpdateTechSideNoteListAction,
                technologyId: technologyId!,
                newTechnologySideNoteList: newSideNoteList
            } as IUpdateTechSideNoteListAction);
        }
    }, [caseDispatch]);

    const receiveDeletedSideNote = useCallback((deletedSideNote: TSideNoteDTO) => {
        // dispatch the removing of a side note in the SideNotesContext
        const newSideNoteList: TSideNoteDTO[] = deleteSideNote(deletedSideNote);
        updateCaseContext(deletedSideNote.noteType, deletedSideNote.technologyId, newSideNoteList);
    }, [deleteSideNote, updateCaseContext]);

    const receiveUpdatedSideNote = useCallback((updatedSideNote: TSideNoteDTO) => {
        // dispatch the update of a side note in the SideNotesContext
        const newSideNoteList: TSideNoteDTO[] = updateSideNote(updatedSideNote);
        updateCaseContext(updatedSideNote.noteType, updatedSideNote.technologyId, newSideNoteList);
    }, [updateSideNote, updateCaseContext]);

    const receiveNewSideNote = useCallback((newSideNote: TNewSideNoteDTO) => {
        // safety-check
        if (!newSideNote.newSideNote) return;

        // new side note is a reply
        if (newSideNote.parentSideNote) {
            // dispatch the addition of a side note in the SideNotesContext
            const newSideNoteList: TSideNoteDTO[] = addReplyToSideNote(newSideNote.parentSideNote, newSideNote.newSideNote);
            updateCaseContext(newSideNote.newSideNote.noteType, newSideNote.newSideNote.technologyId, newSideNoteList);
        // not a reply
        } else {
            // dispatch the addition of a side note in the SideNotesContext
            const newSideNoteList: TSideNoteDTO[] = unshiftSideNote(newSideNote.newSideNote);
            updateCaseContext(newSideNote.newSideNote.noteType, newSideNote.newSideNote.technologyId, newSideNoteList);
        }
    }, [addReplyToSideNote, unshiftSideNote, updateCaseContext]);

    const receiveNotificationMessage = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);

    // Ref
    const receiveDeletedSideNoteRef = useRef(receiveDeletedSideNote);
    const receiveNewSideNoteRef = useRef(receiveNewSideNote);
    const receiveUpdatedSideNoteRef = useRef(receiveUpdatedSideNote);
    const receiveNotificationMessageRef = useRef(receiveNotificationMessage);

    // Logic
    useEffect(() => {
        receiveDeletedSideNoteRef.current = receiveDeletedSideNote;
        receiveNewSideNoteRef.current = receiveNewSideNote;
        receiveUpdatedSideNoteRef.current = receiveUpdatedSideNote;
        receiveNotificationMessageRef.current = receiveNotificationMessage;
    });

    useEffect(() => {
        const receiveDeletedSideNoteLastRef = (deletedSideNote: TSideNoteDTO) => receiveDeletedSideNoteRef.current(deletedSideNote);
        const receiveNewSideNoteLastRef = (newSideNote: TNewSideNoteDTO) => receiveNewSideNoteRef.current(newSideNote);
        const receiveUpdatedSideNoteLastRef = (updatedSideNote: TSideNoteDTO) => receiveUpdatedSideNoteRef.current(updatedSideNote);
        const receiveNotificationMessageLastRef = (notificationMessage: TNotificationMessageDTO) => receiveNotificationMessageRef.current(notificationMessage);

        webSocketController.addHandler(`${HubConstants.CREATE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
            receiveNewSideNoteLastRef);
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
            receiveUpdatedSideNoteLastRef);
        webSocketController.addHandler(`${HubConstants.DELETE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
            receiveDeletedSideNoteLastRef);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
            receiveNotificationMessageLastRef);

        return () => {
            webSocketController.removeHandler(`${HubConstants.CREATE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
                receiveNewSideNoteLastRef);
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
                receiveUpdatedSideNoteLastRef);
            webSocketController.removeHandler(`${HubConstants.DELETE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
                receiveDeletedSideNoteLastRef);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${HubConstants.SIDE_NOTE_RESOURCE_NAME_PREFIX}${sideNotesType}`, 
                receiveNotificationMessageLastRef);
        };
    }, [webSocketController, sideNotesType]);

    return (
        <SideNotesContext.Provider value={{ sideNotes, setNewSideNotes }}>
            {children}
        </SideNotesContext.Provider>
    );
};